import classNames from 'classnames';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentLanguage, useTranslator } from 'tradera/lang/translate';
import { mapCountryCodeToCountryKey } from 'tradera/localization/countries';
import { mapLanguageCodeToLanguageKey } from 'tradera/localization/map-language-code-to-language-key';
import { selectMemberCountryCodeIso2 } from 'tradera/state/member/selectors';
import { webApiClient } from 'tradera/utils/http';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { getLanguageFromCountryCode } from 'tradera/localization/languagecode';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';

type ItemLocalizationProps = {
    itemLocalization: {
        selectedDescriptionLanguageCodeIso2: string;
        shippingFromCountryCodeIso2: string;
    };
};

export const ItemLocalization = ({
    itemLocalization
}: ItemLocalizationProps) => {
    const memberCountryCode = useSelector(selectMemberCountryCodeIso2);
    const { defaultCountryCodeIso2, defaultLanguageCodeIso2 } =
        useDefaultItemLocalizationSettingValues();
    const [
        selectedDescriptionLanguageCodeIso2,
        setSelectedDescriptionLanguageCodeIso2
    ] = useState(
        itemLocalization.selectedDescriptionLanguageCodeIso2 ||
            defaultLanguageCodeIso2
    );

    const [shippingFromCountryCodeIso2, setShippingFromCountryCodeIso2] =
        useState(
            itemLocalization.shippingFromCountryCodeIso2 ||
                defaultCountryCodeIso2
        );

    if (isSwedenCountryCodeOrUndefined(memberCountryCode)) {
        return null;
    }

    const onSelectedDescriptionLanguageChange = (languageCodeIso2: string) => {
        webApiClient.put('/member/profile/selected-description-language', {
            languageCodeIso2
        });
        setSelectedDescriptionLanguageCodeIso2(languageCodeIso2);
    };

    const onShippingFromCountryChange = (countryCodeIso2: string) => {
        webApiClient.put('/member/profile/shipping-from-country', {
            countryCodeIso2
        });
        setShippingFromCountryCodeIso2(countryCodeIso2);
    };
    return (
        <ItemLocalizationSettings
            selectedDescriptionLanguageCodeIso2={
                selectedDescriptionLanguageCodeIso2
            }
            shippingFromCountryCodeIso2={shippingFromCountryCodeIso2}
            onSelectedDescriptionLanguageChange={
                onSelectedDescriptionLanguageChange
            }
            onShippingFromCountryChange={onShippingFromCountryChange}
        />
    );
};

export const getDefaultSelectedDescriptionLanguageCodeIso2 = (
    memberLanguageCodeIso2: string | null,
    memberCountryCodeIso2: string,
    isFrenchDutchSelectable: boolean
) => {
    const countryLanguageCode = getLanguageFromCountryCode(
        memberCountryCodeIso2,
        isFrenchDutchSelectable
    );

    return memberLanguageCodeIso2 === countryLanguageCode
        ? countryLanguageCode
        : 'sv';
};

export const getDefaultShippingFromCountryCodeIso2 = (
    memberCountryCodeCodeIso2: string
) => {
    return ['DK', 'SE'].includes(memberCountryCodeCodeIso2)
        ? memberCountryCodeCodeIso2
        : null;
};

export const useDefaultItemLocalizationSettingValues = (): {
    defaultCountryCodeIso2: string | null;
    defaultLanguageCodeIso2: string;
} => {
    const countryCode = useSelector(selectMemberCountryCodeIso2);
    const { language } = useCurrentLanguage();
    const isFrenchDutchSelectable = useIsFeatureEnabled(
        'french-dutch-selectable'
    );

    return {
        defaultCountryCodeIso2:
            getDefaultShippingFromCountryCodeIso2(countryCode),
        defaultLanguageCodeIso2: getDefaultSelectedDescriptionLanguageCodeIso2(
            language,
            countryCode,
            isFrenchDutchSelectable
        )
    };
};

type ItemLocalizationSettingsProps = {
    selectedDescriptionLanguageCodeIso2: string;
    shippingFromCountryCodeIso2: string | null;
    onSelectedDescriptionLanguageChange: (languageCodeIso2: string) => void;
    onShippingFromCountryChange: (countryCodeIso2: string) => void;
    narrowSelector?: boolean;
};
export const ItemLocalizationSettings = ({
    selectedDescriptionLanguageCodeIso2,
    shippingFromCountryCodeIso2,
    onSelectedDescriptionLanguageChange,
    onShippingFromCountryChange,
    narrowSelector = false
}: ItemLocalizationSettingsProps) => {
    const { t } = useTranslator();
    const memberCountryCode = useSelector(selectMemberCountryCodeIso2);
    const isFrenchDutchSelectable = useIsFeatureEnabled(
        'french-dutch-selectable'
    );

    const descriptionLanguages = [
        ...new Set([
            getLanguageFromCountryCode(
                memberCountryCode,
                isFrenchDutchSelectable
            ),
            'sv',
            'en'
        ])
    ].map((code) => getLanguageOption(code, t));

    const shippingFromCountries = [
        getCountryOption('SE', t),
        getCountryOption('DK', t)
    ];

    const labelClassName = classNames(
        'col-12',
        narrowSelector ? 'col-md-8' : 'col-md-6'
    );
    const selectorClassName = classNames(
        'col-12',
        narrowSelector ? 'col-md-4' : 'col-md-6'
    );

    const showShippingFrom = ['DK', 'SE'].includes(memberCountryCode);
    const topRowClassName = classNames('row', showShippingFrom && 'mb-4');

    return (
        <div className="py-1">
            <div className={topRowClassName}>
                <div className={labelClassName}>
                    <label
                        htmlFor="SelectedDescriptionLanguageCodeIso2"
                        className="font-weight-bold size-paris">
                        {t('myt_settings_selectedDescriptionLanguage')}
                    </label>
                    <div className="size-oslo text-gray-600 mb-1 mb-md-0">
                        {t('myt_settings_selectedDescriptionLanguage_info')}
                    </div>
                </div>
                <div className={selectorClassName}>
                    <select
                        id="SelectedDescriptionLanguageCodeIso2"
                        className="form-control single-select text-inter-medium"
                        value={selectedDescriptionLanguageCodeIso2}
                        onChange={(e) =>
                            onSelectedDescriptionLanguageChange(e.target.value)
                        }>
                        {descriptionLanguages.map(({ value, text }) => (
                            <option key={value} value={value}>
                                {text}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {showShippingFrom && shippingFromCountryCodeIso2 && (
                <div className="row">
                    <div className={labelClassName}>
                        <label
                            htmlFor="ShippingFromCountryCodeIso2"
                            className="font-weight-bold size-paris">
                            {t('myt_settings_shippingFromCountry')}
                        </label>
                        <div className="size-oslo text-gray-600 mb-1 mb-md-0">
                            {t('myt_settings_shippingFromCountry_info')}
                        </div>
                    </div>
                    <div className={selectorClassName}>
                        <select
                            id="ShippingFromCountryCodeIso2"
                            className="form-control single-select text-inter-medium"
                            value={shippingFromCountryCodeIso2}
                            onChange={(e) =>
                                onShippingFromCountryChange(e.target.value)
                            }>
                            {shippingFromCountries.map(({ value, text }) => (
                                <option key={value} value={value}>
                                    {text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};

type Option = { value: string; text: string };

const getLanguageOption = (code: string, t: TFunction): Option => ({
    value: code,
    text: t(mapLanguageCodeToLanguageKey(code))
});

const getCountryOption = (code: string, t: TFunction): Option => ({
    value: code,
    text: t(mapCountryCodeToCountryKey(code))
});
